import React from "react";
import styled from 'styled-components';
import { Button } from '@mui/material';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { CameraIcon } from '../../components/icons/CameraIcon/CameraIcon';
import { DownloadIcon } from '../../components/icons/DownloadIcon/DownloadIcon';
import logoComTextoRoxa from '../../assets/images/logos/logo_com_texto_roxa.png';
import { WaitingList } from '../WaitingPatientsList/WaitingPatientsList';
import { WaitingListPreview } from '../WaitingPatientsList/WaitingListPreview';
import { ITelemedicineConfig } from "../../common/interfaces/ITelemedicineConfig";

interface TelemedicineConfiguration {
  termo_profissional?: string;
  termo_geral?: string;
  configuration?: ITelemedicineConfig;
}

interface TelemedicineData {
  permission: string;
  hashedId?: string;
  telemedicineConfiguration: TelemedicineConfiguration;
}

interface MenuRightProps {
  onJoinButtonClick: () => void;
  roomIsEmpty: boolean;
  telemedicineData: TelemedicineData;
  termoConsentimento: string;
}

export const MenuRight: React.FC<MenuRightProps> = ({
  onJoinButtonClick,
  roomIsEmpty,
  telemedicineData,
  termoConsentimento
}) => {
  const { permission, hashedId } = telemedicineData;

  return (
    <MenuRightContainer>
      <LogoImg src={logoComTextoRoxa} alt="Logo" />
      <Title>Entrar no teleatendimento</Title>
      <Content>
        <JoinButton variant="contained" onClick={onJoinButtonClick} disabled={roomIsEmpty}>
          <div style={{ margin: "4px 4px 5px 4px" }}>
            {roomIsEmpty ? <Spinner /> : <CameraIcon />}
          </div>
          Entrar na sala
        </JoinButton>
        {permission === "patient" && (
          <Termo>
            <Subtitle>
              Ao clicar em entrar na sala você aceita o{" "}
            </Subtitle>
            <TermoConsentimentoLink
              href={
                termoConsentimento ||
                "https://amplimedimggerais.s3-sa-east-1.amazonaws.com/TERMO+DE+CONSENTIMENTO+LIVRE+E+ESCLARECIDO.pdf"
              }
              target="_blank"
            >
              Termo de consentimento <FaExternalLinkAlt size={12} />
            </TermoConsentimentoLink>
          </Termo>
        )}
        {permission === "professional" && telemedicineData.telemedicineConfiguration.configuration?.sala_espera && (
          <WaitingListPreviewWrapper>
            <WaitingListPreview />
          </WaitingListPreviewWrapper>
        )}
      </Content>
      {permission === "patient" && (
        <Footer>
          <DividerContainer>
            <DividerLine />
            <DividerText>Ou</DividerText>
            <DividerLine />
          </DividerContainer>
          <ActionButton
            variant="contained"
            onClick={() => window.open("https://ampli.li/app-link-telemedicina-entrar")}
          >
            <div style={{ margin: "6px 4px 0px 0px" }}>
              <DownloadIcon />
            </div>
            Baixar o app
          </ActionButton>
          <TextWrapper>
            Após baixar o app Amplimed, acesse o menu
            <br />
            Teleatendimento e insira o código abaixo:
            <AppCode>{hashedId || ""}</AppCode>
          </TextWrapper>
        </Footer>
      )}
    </MenuRightContainer>
  );
};

export default MenuRight;

const MenuRightContainer = styled.div`
  height: 100%;
  width: 20%;
  min-width: 240px;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  margin: 0;
  @container (max-width: 560px) {
    width: 100%;
    height: auto;
    box-shadow: none;
  }
`;

const WaitingListPreviewWrapper = styled.div`
  display: block;

  @media (max-width: 560px) {
    display: none;
  }
`;

const LogoImg = styled.img`
  width: 60%;
  display: flex;
  justify-content: center;
  padding: 8%;
  @container (max-width: 560px) {
    display: none;
  }
`;

const Title = styled.h2`
  color: #2a3142;
  font-weight: 400;
  font-size: 22px;
  margin: 0;
  text-align: center;
  width: 100%;

  @container (max-width: 560px) {
    display: none;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const JoinButton = styled(Button)`
  && {
    background: #00b19d;
    color: #ffffff;
    font-weight: 700;
    height: 60px;
    justify-self: end;

    &:hover {
      background-color: #8ceee3;
    }

    @container (max-width: 560px) {
      width: 100%;
      height: 40px;
      z-index: 2;
      bottom: 2dvh;
    }
  }
`;

const Termo = styled.div`
  padding: 10px;
  text-align: center;
  z-index: 2;
`;

const TermoConsentimentoLink = styled.a`
  color: ${(props) => props.theme.colors.primary};
  font-size: 12px;
  font-weight: 300;
  svg {
    margin-left: 5px;
  }
`;

const Subtitle = styled.p`
  color: #4c5667;
  font-size: 12px;
  font-weight: 300;
  margin: 0;
  padding-top: 10px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const DividerContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  margin: 0 10px;
  background: #2a314233;
  width: 100%;
`;

const DividerText = styled.div`
  color: #2a3142;
`;

const ActionButton = styled(Button)`
  && {
    background-color: transparent;
    border: 1px solid #6c757d;
    color: #6c757d;
    font-family: inherit;
    font-size: 1rem;
    line-height: 1.5;
    text-transform: none;
    margin-left: 10px;
    padding: 8px 24px;
    z-index: 2;

    &:hover {
      background-color: #cbcfd3;
    }
  }

  @container (max-width: 560px) {
    width: 100%;
    height: 40px;
  }
`;

const TextWrapper = styled.p`
  margin: 0 0 10px 0;
  font-weight: 300;
  font-size: 12px;
  color: #4c5667;
  text-align: center;
`;

const AppCode = styled.h1`
  color: #2a3142;
  font-weight: bolder;
  font-size: 20px;
  margin: 0 0 30px 0;
`;

const Spinner = styled("div")`
  border: 6px solid rgba(255, 255, 255, 0.3);
  border-top: 6px solid #fff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
